/**
 * The subscribe funnel's shape (ESCLOUD-286, ESCLOUD-332): one declaration of
 * which steps exist and in what order, so a screen names the step it *is*
 * instead of the index it happens to sit at.
 *
 * This is funnel domain data, not presentation: the stepper renders it, and
 * each screen of the funnel asks it where it is.
 *
 * Funnel v2: pricing, account, payment, activating, profile (ESCLOUD-332)
 */

export interface IFunnelStep<K extends string> {
    key: K;
    label: string;
}

export type TSubscribeStepKey = 'account' | 'payment';

/** The stepper covers only the paid part; the profile step has no stepper. */
export const SUBSCRIBE_STEPS: readonly IFunnelStep<TSubscribeStepKey>[] = [
    { key: 'account', label: 'Account' },
    { key: 'payment', label: 'Payment' },
];

/** A free plan never shows Payment: one step, and Continue reads "Create workspace". */
export const FREE_SUBSCRIBE_STEPS: readonly IFunnelStep<TSubscribeStepKey>[] = [
    { key: 'account', label: 'Account' },
];

export const PRICING_ROUTE = '/pricing';
export const SUBSCRIBE_ACCOUNT_ROUTE = '/subscribe/account';
export const SUBSCRIBE_PAYMENT_ROUTE = '/subscribe/payment';
export const SUBSCRIBE_ACTIVATING_ROUTE = '/subscribe/activating';

/**
 * Where a just-activated workspace opens. `WelcomeProfilePage` itself passes
 * straight through when the profile is already complete, so this one
 * constant covers both the buyer, whose profile the payment step already
 * collected, and an invited member landing here for the first time. It carries
 * no tenant segment, so `postActivationHref` returns it unchanged.
 */
export const POST_ACTIVATION_ROUTE = '/welcome/profile';

export function postActivationHref(tenantId: string): string {
    return POST_ACTIVATION_ROUTE.replace(':tenantId', encodeURIComponent(tenantId));
}

export function subscribeAccountHref(planId: string | null): string {
    return planId ? `${SUBSCRIBE_ACCOUNT_ROUTE}?plan=${encodeURIComponent(planId)}` : SUBSCRIBE_ACCOUNT_ROUTE;
}

export function activatingHref(intentId: string): string {
    return `${SUBSCRIBE_ACTIVATING_ROUTE}?intent=${encodeURIComponent(intentId)}`;
}

/** The footer's position label in sentence case: "Step 1 of 2". */
export function stepCounter<K extends string>(steps: readonly IFunnelStep<K>[], key: K): string {
    return `Step ${steps.findIndex((step) => step.key === key) + 1} of ${steps.length}`;
}

/**
 * Where an abandoned purchase resumes: the payment page once a session was
 * created, the account step before that.
 */
export function resumeRouteForIntent(intent: { hasCheckoutSession: boolean }): string {
    return intent.hasCheckoutSession ? SUBSCRIBE_PAYMENT_ROUTE : SUBSCRIBE_ACCOUNT_ROUTE;
}
